// src/components/Pentests/PentestCreateModal.js
//
// Modal for creating a new Penetration Test. Replaces the old standalone
// /pentests/new page; matches the dialog pattern used for beacon deploy.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ClipboardCheck, Plus, X } from 'lucide-react';
import { pentestsAPI } from '../../services/api';
import { PrimaryCTA } from '../common/Shinobi';
import { WASABI_VAULTS, filterVaults, vaultDisplayName } from './wasabiVaults';

const TYPE_OPTIONS = [
  { value: 'external', label: 'External', desc: 'Outside-looking-in, internet-facing perimeter' },
  { value: 'internal', label: 'Internal', desc: 'Assumed breach, post-perimeter perspective' },
  { value: 'third_party_external', label: '3rd-party External', desc: 'External engagement performed by third party' },
  { value: 'third_party_internal', label: '3rd-party Internal', desc: 'Internal engagement performed by third party' },
];

const CLASSIFICATION_OPTIONS = [
  { value: 'c1', label: 'C1 - Public' },
  { value: 'c2', label: 'C2 - Internal' },
  { value: 'c3', label: 'C3 - Confidential' },
  { value: 'c4', label: 'C4 - Secret' },
];

export default function PentestCreateModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [type, setType] = useState('external');
  const [classification, setClassification] = useState('c3');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setType('external');
      setClassification('c3');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await pentestsAPI.create({
        name: name.trim(),
        type,
        classification,
      });
      onClose?.();
      navigate(`/pentests/${created.id}/pre-engagement`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-2xl max-h-[92vh] overflow-auto rounded-2xl border border-cyber-300/60 bg-cyber-50 shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyber-300/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl border border-wasabi-300 bg-wasabi-100 flex items-center justify-center text-shinobi-green">
              <ClipboardCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">New engagement</div>
              <h2 className="text-lg font-semibold text-slate-900">Create Penetration Test</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            className="p-1.5 rounded text-slate-500 hover:text-slate-900 hover:bg-cyber-200/40"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-slate-500">
            Pick a Wasabi vault or type a custom name. Type, classification, and the questionnaire come next.
          </p>

          <VaultPicker value={name} onChange={setName} />

          <div>
            <label className="block text-sm font-medium text-slate-800 mb-2">Type</label>
            <div className="grid grid-cols-2 gap-3">
              {TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setType(opt.value)}
                  className={`text-left rounded-lg border p-3 transition ${
                    type === opt.value
                      ? 'border-shinobi-green bg-wasabi-100'
                      : 'border-cyber-300/60 bg-cyber-100 hover:border-cyber-300'
                  }`}
                >
                  <div className="font-semibold text-slate-900">{opt.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-800 mb-2">Classification</label>
            <select
              value={classification}
              onChange={(e) => setClassification(e.target.value)}
              className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
            >
              {CLASSIFICATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-cyber-300/40 bg-cyber-100/40">
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            className="px-4 py-2 rounded-lg text-slate-700 hover:text-slate-900"
          >
            Cancel
          </button>
          <PrimaryCTA
            variant="green"
            type="submit"
            loading={submitting}
            icon={Plus}
            iconPosition="left"
          >
            Create
          </PrimaryCTA>
        </div>
      </form>
    </div>
  );
}

// Combobox: pick a known Wasabi vault from the catalog, or freely type a custom name.
function VaultPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const matches = filterVaults(value);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    setHighlight(0);
  }, [value]);

  const select = (vault) => {
    onChange(vaultDisplayName(vault));
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (matches[highlight]) {
        e.preventDefault();
        select(matches[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="block text-sm font-medium text-slate-800 mb-2">Name</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="e.g., Wasabi Storage Region us-west-1 (Hillsboro)"
          className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 pr-10 text-slate-900 focus:border-shinobi-green outline-none"
          required
          autoFocus
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-800"
          aria-label="Toggle vault list"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-cyber-300/60 bg-cyber-100 shadow-2xl">
          {matches.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-500">
              No matching vault. Hit Enter to keep your custom name.
            </div>
          ) : (
            matches.map((v, idx) => (
              <button
                key={v.region}
                type="button"
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => { e.preventDefault(); select(v); }}
                className={`w-full text-left px-3 py-2 border-b border-cyber-300/30 last:border-b-0 ${
                  idx === highlight ? 'bg-wasabi-100 text-wasabi-700' : 'text-slate-800 hover:bg-cyber-200/40'
                }`}
              >
                <div className="text-sm font-medium">{vaultDisplayName(v)}</div>
                <div className="text-xs text-slate-500 mt-0.5 font-mono">
                  {v.vault} · site {v.site} · {v.location}
                </div>
              </button>
            ))
          )}
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 border-t border-cyber-300/30 bg-cyber-50/50">
            {WASABI_VAULTS.length} preset vaults. Type to filter or enter a custom name.
          </div>
        </div>
      )}
    </div>
  );
}
